import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { Button, Form, Input, Modal, Space, Typography } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { formatMoney, onModalOk } from "@/shared/lib";

import { useProcessDeposit } from "../model/useProcessDeposit";

/**
 * Кнопки обработки заявки.
 *
 * Подтверждение спрашивает согласие, а отклонение — обязательный комментарий:
 * пользователь увидит его в ленте активности, и «отклонено» без причины
 * гарантированно возвращается вопросом в поддержку.
 */
export function DepositActions({
  id,
  userId,
  amount,
  disabled,
}: {
  id: number;
  userId: number;
  amount: string;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const { approve, reject } = useProcessDeposit();
  const [rejecting, setRejecting] = useState(false);
  const [form] = Form.useForm<{ comment: string }>();
  const [modal, contextHolder] = Modal.useModal();

  const confirmApprove = () => {
    void modal.confirm({
      title: t("deposits.approve"),
      content: t("deposits.approveConfirm", { amount: formatMoney(amount) }),
      okText: t("common.confirm"),
      cancelText: t("common.cancel"),
      onOk: () => approve.mutateAsync({ id, userId }),
    });
  };

  return (
    <Space>
      {contextHolder}
      <Button
        type="primary"
        icon={<CheckOutlined aria-hidden />}
        disabled={disabled}
        loading={approve.isPending}
        onClick={confirmApprove}
      >
        {t("common.confirm")}
      </Button>
      <Button
        danger
        icon={<CloseOutlined aria-hidden />}
        disabled={disabled}
        onClick={() => {
          setRejecting(true);
        }}
      >
        {t("common.reject")}
      </Button>

      <Modal
        open={rejecting}
        title={t("deposits.rejectTitle")}
        okText={t("common.reject")}
        cancelText={t("common.cancel")}
        okButtonProps={{ danger: true, loading: reject.isPending }}
        onCancel={() => {
          setRejecting(false);
        }}
        onOk={onModalOk(form, async (values) => {
          await reject.mutateAsync({ id, userId, comment: values.comment });
          form.resetFields();
          setRejecting(false);
        })}
      >
        <Typography.Paragraph type="secondary">
          {t("common.commentRequired")}
        </Typography.Paragraph>
        <Form form={form} layout="vertical">
          <Form.Item
            name="comment"
            label={t("common.comment")}
            rules={[{ required: true, min: 3, message: t("common.commentRequired") }]}
          >
            <Input.TextArea rows={3} autoFocus />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
